<template>
    <div>
        <content-placeholders v-if="loading">
            <content-placeholders-text :lines="3"/>
        </content-placeholders>
        <div v-else-if="available_vocabulary && available_vocabulary.length">
            <b-list-group>
                <b-list-group-item v-for="(term, index) in available_vocabulary" :key="index">
                    <div>
                        <strong class="text-nowrap">{{ term.term }}</strong>
                    </div>
                    <div v-for="(definition, index) in term.definitions" :key="'definition' + index">
                        <div class="d-flex align-items-center">
                            <div>
                                <b-form-checkbox
                                    v-model="definition.linked"
                                    :checked="definition.linked"
                                    switch
                                    @change="linkDefinition($event, definition)"
                                    :disabled="disabled"
                                ></b-form-checkbox>
                            </div>
                            <div>
                                {{ definition.definition }}
                                <span
                                    v-if="definition.image && definition.image.src"
                                    :id="`popover-${definition.definition_id}`"
                                    class="text-primary"
                                >
                                    [View Image]
                                </span>
                            </div>
                            <b-popover
                                v-if="definition.image && definition.image.src"
                                :target="`popover-${definition.definition_id}`" triggers="hover"
                                placement="right" class="mt-2 d-flex justify-content-center">
                                <template #title>
                                    Image Preview
                                </template>
                                <b-link :href="definition.image.src" target="_blank">
                                    <b-img
                                        :src="definition.image.src"
                                        width="200"
                                        height="auto"
                                        class="p-0 m-0"
                                        thumbnail
                                    />
                                </b-link>
                            </b-popover>
                            <div class="ml-auto d-flex flex-row">
                                <div class="mr-2">
                                    <div v-if="unit.articles_visible">
                                        <a
                                            href="#"
                                            v-scroll-to="{
                                            el: '#unit' + unit.id + 'article' + definition.article_id,
                                            offset: -144
                                        }"
                                        >
                                            {{ definition.article_title }}
                                        </a>
                                    </div>
                                    <div v-else class="text-muted">
                                        {{ definition.article_title }}
                                    </div>
                                </div>
                                <b-btn
                                    variant="outline-primary"
                                    size="sm"
                                    :disabled="disabled"
                                    @click="showEditTermModal(term)"
                                >
                                    <font-awesome-icon :icon="['fad', 'edit']" fixed-width/>
                                    Edit 
                                </b-btn>
                                <b-btn
                                    variant="danger"
                                    size="sm"
                                    class="ml-2"
                                    :disabled="disabled"
                                    @click="removeTermModal(term)"
                                >
                                    <font-awesome-icon :icon="['fad', 'trash']" fixed-width/>
                                    Remove 
                                </b-btn>
                            </div>
                        </div>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </div>
        <div v-else>
            <b-alert variant="danger" class="mb-0" show>
                0 Vocabulary Terms have been added to articles in this week
            </b-alert>
        </div>
        <vocabulary-term-modal
            v-model="show_vocabulary_term_modal"
            :vocabulary_term="selected_term"
            :vocabulary_terms="vocabulary"
            @update="editTerm"
            @hide="show_vocabulary_term_modal = false"
        />
    </div>
</template>

<script>
import VocabularyTermModal from '@/components/modals/article/VocabularyTermModal'
import moment from 'moment'

export default {
    components: {
        VocabularyTermModal,
    },
    data: () => ({
        loading: false,
        vocabulary: [],
        show_vocabulary_term_modal: false,
        selected_term: {},
    }),
    props: {
        unit: Object,
        disabled: Boolean,
    },
    methods: {
        async getVocabulary() {
            try {
                this.loading = true
                const response = await this.axios.get(`/api/units/vocabulary/${this.unit.id}`)
                if (response.data) {
                    this.vocabulary = response.data
                    this.vocabulary = _.orderBy(this.vocabulary, 'term')
                }

                _.forEach(this.vocabulary, term => {
                    _.forEach(term.definitions, definition => {
                        definition.linked = definition.linked === 1;
                    })
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to load vocabulary for unit: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading = false
            }

        },
        linkDefinition(linked, definition) {
            if (linked) {
                this.addVocabulary(definition)
            } else {
                this.removeVocabulary(definition)
            }
        },
        showEditTermModal(term) {
            this.selected_term = {
                ...term,
                image: term.image ? JSON.parse(term.image) : null,
            }
            this.show_vocabulary_term_modal = true
        },
        async removeTermModal(term){
            let message = 'This action will remove the term from the Week and the Article. Are you sure you wish to continue?'
            const termInUse = await this.$store.dispatch('termInCrossword', {unitId: this.unit.id, termId: term.vocabulary_term_id})
            if (termInUse) {
                message += '<p class="mt-3 mb-0"><strong>Warning:</strong> This term is used by the crossword for this week. Removing it will reset the crossword.</p>';
           }


            let article_id = term.article_id;
            let vocabulary_term_id = term.vocabulary_term_id;
            const result = await this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Remove Term',
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
            if (result.value) {
                try {
                    // articles/remove_vocabulary/
                    await this.axios.post(`/api/articles/remove_vocabulary/${article_id}`, {
                        article_id: term.article_id,
                        vocabulary_term_id: vocabulary_term_id,
                    })
                    if (termInUse) {
                        await this.$store.dispatch('saveCrossword', {json: null, unit_id: this.unit.id})
                    }
                } catch (e) {
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Failed to remove vocabulary from article: ${e.message}`,
                        },
                        time: moment().toISOString(),
                    })
                } finally {
                    await this.getVocabulary()
                }    
            }
        },
        async editTerm(term) {
            try {
                await this.axios.patch(`/api/units/edit_vocabulary/${this.unit.id}`, {
                    vocabulary_term_id: term.vocabulary_term_id,
                    vocabulary_term: term.term,
                    definition_id: term.definition_id,
                    definition: term.definition,
                    image: term.image,
                    sentence: term.sentence,
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to edit vocabulary for unit: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                await this.getVocabulary()
                this.show_vocabulary_term_modal = false
            }
        },
        async addVocabulary(definition) {
            try {
                await this.axios.post(`/api/units/add_vocabulary/${this.unit.id}`, {
                    definition_id: definition.definition_id,
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to add vocabulary to unit: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async removeVocabulary(definition) {
            try {
                await this.axios.post('/api/units/remove_vocabulary/' + this.unit.id, {
                    definition_id: definition.definition_id,
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to remove vocabulary from unit: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
    },
    computed: {
        available_vocabulary() {
            let vocabulary = []

            vocabulary = _.orderBy(this.vocabulary, 'term')

            _.forEach(vocabulary, term => {
                let definitions = null

                if (term.definitions) {
                    definitions = _.uniqBy(term.definitions, 'definition_id')
                    definitions = _.orderBy(definitions, 'definition_id')

                    this.$set(term, 'definitions', definitions)
                }
            })

            vocabulary = _.orderBy(vocabulary, [vocabulary => vocabulary.term.toLowerCase()], 'asc')
            return vocabulary
        },
    },
    async mounted() {
        await this.getVocabulary()
    },
}
</script>
